// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
/**
 * Landafhankelijke voorbeelden/placeholders voor de module Tweede woning.
 *
 * Uitsluitend voorbeeldteksten (placeholders) - geen opgeslagen gegevens.
 * Per landcode een passende set, zodat een Nederlandse tweede woning geen
 * buitenlandse (bijv. Spaanse) voorbeelden meer toont. Eenvoudig uitbreidbaar:
 * voeg een landcode toe of pas een set aan.
 *
 * Valt terug op `default` (neutraal) wanneer een land niet is gedefinieerd.
 */
export interface SecondHomeExamples {
  contactName:   string
  contactPhone:  string
  gpName:        string
  gpPractice:    string
  gpPhone:       string
  gpCity:        string
  docOwnership:  string
  docKeys:       string
  docAlarm:      string
  docInsurance:  string
}

const NL: SecondHomeExamples = {
  contactName:  'Naam beheerder of buurvrouw',
  contactPhone: '072-1234567',
  gpName:       'Dr. Jansen',
  gpPractice:   'Huisartsenpraktijk De Brink',
  gpPhone:      '072-1234567',
  gpCity:       'Heiloo',
  docOwnership: "Bij notaris De Vries in Alkmaar",
  docKeys:      'Extra sleutel bij buurvrouw of familie',
  docAlarm:     "Opgeborgen in de map 'Woning'",
  docInsurance: "Map 'Verzekeringen', polisnummer 123456",
}

const ES: SecondHomeExamples = {
  contactName:  'Naam beheerder of buurman',
  contactPhone: '+34 6...',
  gpName:       'Dr. García',
  gpPractice:   'Centro Médico',
  gpPhone:      '+34 6...',
  gpCity:       'Madrid',
  docOwnership: 'Bij notaris García in Madrid',
  docKeys:      'Extra sleutel bij de buren',
  docAlarm:     "Opgeborgen in de map 'Woning'",
  docInsurance: "Map 'Verzekeringen', polisnummer 123456",
}

const BE: SecondHomeExamples = {
  contactName: 'Naam beheerder of buur', contactPhone: '+32 4...',
  gpName: 'Dr. Janssens', gpPractice: 'Huisartsenpraktijk', gpPhone: '+32 4...', gpCity: 'Antwerpen',
  docOwnership: 'Bij de notaris in Antwerpen', docKeys: 'Extra sleutel bij de buren',
  docAlarm: "Opgeborgen in de map 'Woning'", docInsurance: "Map 'Verzekeringen', polisnummer 123456",
}

const DE: SecondHomeExamples = {
  contactName: 'Naam Hausverwalter of buur', contactPhone: '+49 1...',
  gpName: 'Dr. Müller', gpPractice: 'Hausarztpraxis', gpPhone: '+49 1...', gpCity: 'Berlin',
  docOwnership: 'Bij de Notar in Berlin', docKeys: 'Extra sleutel bij de buren',
  docAlarm: "Opgeborgen in de map 'Woning'", docInsurance: "Map 'Verzekeringen', polisnummer 123456",
}

const FR: SecondHomeExamples = {
  contactName: 'Naam gardien of buur', contactPhone: '+33 6...',
  gpName: 'Dr. Martin', gpPractice: 'Cabinet médical', gpPhone: '+33 6...', gpCity: 'Lyon',
  docOwnership: 'Bij de notaire in Lyon', docKeys: 'Extra sleutel bij de buren',
  docAlarm: "Opgeborgen in de map 'Woning'", docInsurance: "Map 'Verzekeringen', polisnummer 123456",
}

const IT: SecondHomeExamples = {
  contactName: 'Naam beheerder of buur', contactPhone: '+39 3...',
  gpName: 'Dr. Rossi', gpPractice: 'Studio medico', gpPhone: '+39 3...', gpCity: 'Roma',
  docOwnership: 'Bij de notaio in Roma', docKeys: 'Extra sleutel bij de buren',
  docAlarm: "Opgeborgen in de map 'Woning'", docInsurance: "Map 'Verzekeringen', polisnummer 123456",
}

const DEFAULT: SecondHomeExamples = {
  contactName: 'Naam beheerder of contactpersoon', contactPhone: 'Lokaal telefoonnummer',
  gpName: 'Naam huisarts', gpPractice: 'Naam praktijk', gpPhone: 'Lokaal telefoonnummer', gpCity: 'Plaats',
  docOwnership: 'Bijv: bij de notaris ter plaatse', docKeys: 'Bijv: extra sleutel bij de buren',
  docAlarm: "Bijv: opgeborgen in de map 'Woning'", docInsurance: "Bijv: map 'Verzekeringen', polisnummer",
}

const BY_COUNTRY: Record<string, SecondHomeExamples> = {
  NL, BE, DE, FR, ES, IT,
}

/** Geeft de voorbeeldset voor een landcode; valt terug op een neutrale set. */
export function secondHomeExamples(country: string | undefined): SecondHomeExamples {
  if (!country) return NL
  return BY_COUNTRY[country] ?? DEFAULT
}
